import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * PDFFieldHighlighter - Renders highlighting overlays on top of PDF to show where data was extracted
 *
 * @param {Object} fieldConfidence - Textract field confidence data with bounding boxes
 * @param {string|null} highlightedField - The field name to highlight (e.g., "certificateHolder", "insured")
 * @param {number} pdfWidth - Width of the rendered PDF in pixels
 * @param {number} pdfHeight - Height of the rendered PDF in pixels
 * @param {number} pageNumber - Current page number being displayed
 */
export default function PDFFieldHighlighter({
  fieldConfidence,
  highlightedField,
  pdfWidth,
  pdfHeight,
  pageNumber = 1
}) {
  const [highlights, setHighlights] = useState([]);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (!fieldConfidence || !highlightedField || !pdfWidth || !pdfHeight) {
      setHighlights([]);
      return;
    }

    try {
      // Parse fieldConfidence if it's a string
      const confidenceData = typeof fieldConfidence === 'string'
        ? JSON.parse(fieldConfidence)
        : fieldConfidence;

      // Get field data for the highlighted field
      const fieldData = confidenceData[highlightedField];

      if (!fieldData || !fieldData.boundingBox) {
        setHighlights([]);
        return;
      }

      // Textract returns normalized coordinates (0-1 range)
      // We need to convert them to pixel coordinates
      const { boundingBox, page } = fieldData;

      // Only show highlight if it's on the current page
      if (page && page !== pageNumber) {
        setHighlights([]);
        return;
      }

      // Convert normalized coordinates to pixels
      // Textract format: {Left, Top, Width, Height} - all values 0-1
      const highlight = {
        left: boundingBox.Left * pdfWidth,
        top: boundingBox.Top * pdfHeight,
        width: boundingBox.Width * pdfWidth,
        height: boundingBox.Height * pdfHeight,
        fieldName: highlightedField,
        confidence: fieldData.confidence || 1.0
      };

      setHighlights([highlight]);

      // Trigger animation
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 600);
      return () => clearTimeout(timer);

    } catch (error) {
      console.error('Error parsing field confidence data:', error);
      setHighlights([]);
    }
  }, [fieldConfidence, highlightedField, pdfWidth, pdfHeight, pageNumber]);

  if (!highlights.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none', // Allow clicks to pass through
        zIndex: 10
      }}
    >
      {highlights.map((highlight, index) => (
        <Box
          key={`${highlight.fieldName}-${index}`}
          sx={{
            position: 'absolute',
            left: `${highlight.left}px`,
            top: `${highlight.top}px`,
            width: `${highlight.width}px`,
            height: `${highlight.height}px`,
            border: '3px solid #ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.15)',
            borderRadius: '4px',
            animation: animating ? 'pulse 0.6s ease-in-out' : 'none',
            boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'scale(1.05)',
                opacity: 0.8,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1,
              }
            }
          }}
        >
          {/* Confidence badge */}
          <Box
            sx={{
              position: 'absolute',
              top: '-24px',
              left: '0',
              backgroundColor: '#ff6b6b',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {(highlight.confidence * 100).toFixed(0)}% confidence
          </Box>
        </Box>
      ))}
    </Box>
  );
}
